import React, { useCallback, useEffect, useRef } from 'react';
import Icon from './Icon';
import IconButton from './IconButton';

const SAFE_GAP = 8;
const WINDOW_OBSTACLE_SELECTOR = '[data-floating-window-obstacle]';

function getViewportBounds() {
  const viewport = window.visualViewport;
  const left = Number(viewport?.offsetLeft) || 0;
  const top = Number(viewport?.offsetTop) || 0;
  const width = Number(viewport?.width) || window.innerWidth;
  const height = Number(viewport?.height) || window.innerHeight;
  return {
    bottom: top + height,
    height,
    left,
    right: left + width,
    top,
    width,
  };
}

function getObstacleRects() {
  if (typeof document === 'undefined') return [];

  return Array.from(document.querySelectorAll(WINDOW_OBSTACLE_SELECTOR))
    .map(element => element.getBoundingClientRect())
    .filter(rect => rect.width > 0 && rect.height > 0);
}

function overlapsObstacle(position, rect, obstacle) {
  return (
    position.x < obstacle.right + SAFE_GAP
    && position.x + rect.width > obstacle.left - SAFE_GAP
    && position.y < obstacle.bottom + SAFE_GAP
    && position.y + rect.height > obstacle.top - SAFE_GAP
  );
}

function clampPosition(x, y, rect, obstacles = getObstacleRects()) {
  const viewport = getViewportBounds();
  const minimumX = viewport.left + SAFE_GAP;
  const minimumY = viewport.top + SAFE_GAP;
  const maximumX = Math.max(minimumX, viewport.right - rect.width - SAFE_GAP);
  const maximumY = Math.max(minimumY, viewport.bottom - rect.height - SAFE_GAP);
  const boundedPosition = {
    x: Math.min(Math.max(minimumX, x), maximumX),
    y: Math.min(Math.max(minimumY, y), maximumY),
  };
  if (!obstacles.some(obstacle => overlapsObstacle(boundedPosition, rect, obstacle))) {
    return boundedPosition;
  }

  const xCandidates = new Set([boundedPosition.x, minimumX, maximumX]);
  const yCandidates = new Set([boundedPosition.y, minimumY, maximumY]);
  for (const obstacle of obstacles) {
    xCandidates.add(obstacle.left - SAFE_GAP - rect.width);
    xCandidates.add(obstacle.right + SAFE_GAP);
    yCandidates.add(obstacle.top - SAFE_GAP - rect.height);
    yCandidates.add(obstacle.bottom + SAFE_GAP);
  }

  let closestPosition = null;
  let closestDistance = Infinity;
  for (const candidateX of xCandidates) {
    if (candidateX < minimumX || candidateX > maximumX) continue;
    for (const candidateY of yCandidates) {
      if (candidateY < minimumY || candidateY > maximumY) continue;
      const candidate = { x: candidateX, y: candidateY };
      if (obstacles.some(obstacle => overlapsObstacle(candidate, rect, obstacle))) continue;

      const distance = ((candidateX - boundedPosition.x) ** 2) + ((candidateY - boundedPosition.y) ** 2);
      if (distance < closestDistance) {
        closestPosition = candidate;
        closestDistance = distance;
      }
    }
  }

  return closestPosition ?? boundedPosition;
}

function clampSize(
  width,
  height,
  rect,
  minSize = {},
  maxSize = {},
  resizeFromRight = false,
  resizeFromBottom = false,
  scaleSizeRange = false,
) {
  const viewport = getViewportBounds();
  const minWidth = minSize.width ?? 240;
  const minHeight = minSize.height ?? 160;
  const availableWidth = Math.max(
    minWidth,
    resizeFromRight
      ? rect.right - viewport.left - SAFE_GAP
      : viewport.right - rect.left - SAFE_GAP,
  );
  const availableHeight = Math.max(
    minHeight,
    resizeFromBottom
      ? rect.bottom - viewport.top - SAFE_GAP
      : viewport.bottom - rect.top - SAFE_GAP,
  );
  const maxWidth = Math.max(minWidth, Math.min(maxSize.width ?? Infinity, availableWidth));
  const maxHeight = Math.max(minHeight, Math.min(maxSize.height ?? Infinity, availableHeight));

  const sizeRangeWidth = (maxSize.width ?? Infinity) - minWidth;
  const sizeRangeHeight = (maxSize.height ?? Infinity) - minHeight;
  if (scaleSizeRange && Number.isFinite(sizeRangeWidth) && Number.isFinite(sizeRangeHeight)) {
    const currentWidthProgress = sizeRangeWidth > 0 ? (rect.width - minWidth) / sizeRangeWidth : 0;
    const currentHeightProgress = sizeRangeHeight > 0 ? (rect.height - minHeight) / sizeRangeHeight : 0;
    const widthProgress = sizeRangeWidth > 0 ? (width - minWidth) / sizeRangeWidth : 0;
    const heightProgress = sizeRangeHeight > 0 ? (height - minHeight) / sizeRangeHeight : 0;
    const widthMovement = Math.abs(widthProgress - currentWidthProgress);
    const heightMovement = Math.abs(heightProgress - currentHeightProgress);
    const requestedProgress = widthMovement >= heightMovement ? widthProgress : heightProgress;
    const maximumProgress = Math.min(
      1,
      sizeRangeWidth > 0 ? (maxWidth - minWidth) / sizeRangeWidth : 1,
      sizeRangeHeight > 0 ? (maxHeight - minHeight) / sizeRangeHeight : 1,
    );
    const nextProgress = Math.min(Math.max(0, requestedProgress), maximumProgress);

    return {
      width: Math.round(minWidth + sizeRangeWidth * nextProgress),
      height: Math.round(minHeight + sizeRangeHeight * nextProgress),
    };
  }

  return {
    width: Math.round(Math.min(Math.max(minWidth, width), maxWidth)),
    height: Math.round(Math.min(Math.max(minHeight, height), maxHeight)),
  };
}

export default function FloatingWindow({
  anchor,
  children,
  compactContent,
  focused,
  icon,
  id,
  open,
  pinned,
  compact,
  position,
  resizable = false,
  size,
  minSize,
  maxSize,
  scaleSizeRange = false,
  shortcut,
  title,
  zIndex,
  onClose,
  onCompactToggle,
  onFocus,
  onMove,
  onPinToggle,
  onResize,
}) {
  const windowRef = useRef(null);
  const resizeFromRight = !position && anchor.includes('right');
  const resizeFromBottom = !position && anchor.includes('bottom');
  const resizeOrigin = resizeFromRight
    ? (resizeFromBottom ? 'top-left' : 'bottom-left')
    : (resizeFromBottom ? 'top-right' : 'bottom-right');

  const clampCurrentPosition = useCallback(() => {
    if (!windowRef.current || window.innerWidth < 768) return;
    const rect = windowRef.current.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const current = position ?? { x: rect.left, y: rect.top };
    const next = clampPosition(current.x, current.y, rect);
    if (next.x !== current.x || next.y !== current.y) onMove(id, next);
  }, [id, onMove, position]);

  useEffect(() => {
    const viewport = window.visualViewport;
    window.addEventListener('resize', clampCurrentPosition);
    window.addEventListener('orientationchange', clampCurrentPosition);
    viewport?.addEventListener?.('resize', clampCurrentPosition);
    viewport?.addEventListener?.('scroll', clampCurrentPosition);
    return () => {
      window.removeEventListener('resize', clampCurrentPosition);
      window.removeEventListener('orientationchange', clampCurrentPosition);
      viewport?.removeEventListener?.('resize', clampCurrentPosition);
      viewport?.removeEventListener?.('scroll', clampCurrentPosition);
    };
  }, [clampCurrentPosition]);

  useEffect(() => {
    clampCurrentPosition();
  }, [clampCurrentPosition, compact, open, pinned, size]);

  const startDrag = useCallback((event) => {
    if (event.button !== 0 || window.innerWidth < 768 || event.target.closest('button')) return;
    const element = windowRef.current;
    if (!element) return;
    event.preventDefault();
    onFocus(id);
    const rect = element.getBoundingClientRect();
    const obstacles = getObstacleRects();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;

    const move = (moveEvent) => {
      onMove(id, clampPosition(
        moveEvent.clientX - offsetX,
        moveEvent.clientY - offsetY,
        rect,
        obstacles,
      ));
    };
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop, { once: true });
    window.addEventListener('pointercancel', stop, { once: true });
  }, [id, onFocus, onMove]);

  const startResize = useCallback((event) => {
    if (event.button !== 0 || window.innerWidth < 768) return;
    const element = windowRef.current;
    if (!element) return;

    event.preventDefault();
    event.stopPropagation();
    onFocus(id);
    const rect = element.getBoundingClientRect();
    const startX = event.clientX;
    const startY = event.clientY;

    const resize = (moveEvent) => {
      onResize(id, clampSize(
        rect.width + (resizeFromRight ? startX - moveEvent.clientX : moveEvent.clientX - startX),
        rect.height + (resizeFromBottom ? startY - moveEvent.clientY : moveEvent.clientY - startY),
        rect,
        minSize,
        maxSize,
        resizeFromRight,
        resizeFromBottom,
        scaleSizeRange,
      ));
    };
    const stop = () => {
      window.removeEventListener('pointermove', resize);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
    window.addEventListener('pointermove', resize);
    window.addEventListener('pointerup', stop, { once: true });
    window.addEventListener('pointercancel', stop, { once: true });
  }, [id, maxSize, minSize, onFocus, onResize, resizeFromBottom, resizeFromRight, scaleSizeRange]);

  const resizeWithKeyboard = useCallback((event) => {
    if (window.innerWidth < 768 || !['ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowUp'].includes(event.key)) return;
    const element = windowRef.current;
    if (!element) return;

    event.preventDefault();
    const rect = element.getBoundingClientRect();
    const step = event.shiftKey ? 1 : 16;
    const widthDelta = event.key === 'ArrowRight' ? step : event.key === 'ArrowLeft' ? -step : 0;
    const heightDelta = event.key === 'ArrowDown' ? step : event.key === 'ArrowUp' ? -step : 0;
    onFocus(id);
    onResize(id, clampSize(
      rect.width + widthDelta,
      rect.height + heightDelta,
      rect,
      minSize,
      maxSize,
      resizeFromRight,
      resizeFromBottom,
      scaleSizeRange,
    ));
  }, [id, maxSize, minSize, onFocus, onResize, resizeFromBottom, resizeFromRight, scaleSizeRange]);

  if (!open) return null;

  const canResize = resizable && !compact && !pinned;
  const style = { zIndex };
  if (canResize && size) {
    style.width = size.width;
    style.height = size.height;
  }
  if (position) {
    style.left = position.x;
    style.top = position.y;
    style.right = 'auto';
    style.bottom = 'auto';
  }

  return (
    <aside
      aria-label={title}
      className={`pl-window pl-window--${anchor} pl-window--${id}`}
      data-compact={compact}
      data-focused={focused}
      data-pinned={pinned}
      data-resizable={resizable || undefined}
      data-resize-origin={canResize ? resizeOrigin : undefined}
      id={`${id}-window`}
      onPointerDown={() => onFocus(id)}
      ref={windowRef}
      style={style}
    >
      <header
        className="pl-window__header"
        onDoubleClick={() => onCompactToggle(id)}
        onPointerDown={startDrag}
      >
        <Icon name={icon} size={18} />
        <h2 className="pl-window__title">{title}</h2>
        {shortcut ? <kbd className="pl-window__shortcut">{shortcut}</kbd> : null}
        <div className="pl-window__controls">
          <IconButton
            aria-pressed={pinned}
            icon={pinned ? 'unpin' : 'pin'}
            label={pinned ? `Unpin ${title}` : `Pin ${title}`}
            onClick={() => onPinToggle(id)}
          />
          <IconButton
            aria-pressed={compact}
            icon={compact ? 'expand' : 'collapse'}
            label={compact ? `Expand ${title}` : `Compact ${title}`}
            onClick={() => onCompactToggle(id)}
          />
          <IconButton icon="close" label={`Close ${title}`} onClick={() => onClose(id)} />
        </div>
      </header>
      <div className="pl-window__body">
        {compact && compactContent ? compactContent : children}
      </div>
      {canResize ? (
        <button
          aria-keyshortcuts="ArrowUp ArrowDown ArrowLeft ArrowRight"
          aria-label={`Resize ${title} window; use arrow keys`}
          className={`pl-window__resize pl-window__resize--${resizeOrigin}`}
          onKeyDown={resizeWithKeyboard}
          onPointerDown={startResize}
          title="Drag to resize. Use arrow keys for keyboard resizing; hold Shift for 1px steps."
          type="button"
        />
      ) : null}
    </aside>
  );
}
